import type { UpdateManifest } from './UpdateService';

export type UpdateSafetyPhase =
  | 'idle'
  | 'checking'
  | 'manifest_valid'
  | 'signature_verified'
  | 'backup_required'
  | 'ready'
  | 'blocked';

export interface UpdateSafetyContext {
  currentVersion: string;
  tenantId?: string;
  hasActiveSession: boolean;
  diskSpaceOk?: boolean;
}

export interface UpdateSafetyResult {
  phase: UpdateSafetyPhase;
  allowed: boolean;
  reasons: string[];
}

/** Contrôles de sûreté avant application d'une mise à jour. */
export class UpdateStateMachine {
  async evaluate(
    manifest: UpdateManifest | null,
    ctx: UpdateSafetyContext,
  ): Promise<UpdateSafetyResult> {
    const reasons: string[] = [];
    if (!manifest?.is_outdated) {
      return { phase: 'idle', allowed: false, reasons: ['aucune mise à jour disponible'] };
    }
    if (!ctx.hasActiveSession) {
      reasons.push('session administrateur requise');
    }
    if (ctx.diskSpaceOk === false) {
      reasons.push('espace disque insuffisant');
    }

    // Seul un manifeste dont la signature a été vérifiée par le processus principal (clé publique
    // embarquée) peut être installé ; il est revérifié au moment du téléchargement.
    if (manifest.install_mode !== 'signed' || !manifest.signed_manifest) {
      reasons.push('manifeste signé requis');
      return { phase: 'blocked', allowed: false, reasons };
    }

    if (!ctx.tenantId) {
      reasons.push('tenant inconnu — sauvegarde impossible');
    }

    const allowed = reasons.length === 0;
    return {
      phase: allowed ? 'ready' : 'backup_required',
      allowed,
      reasons,
    };
  }
}
